import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  IconButton,
  Button,
  Stack,
  TextField,
  Chip,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// Products carry an `images` array of hosted URLs (see product schema). This
// section collects those URLs and wires them straight into the form so they
// are included in the create/update payload — the first image is the thumbnail.
const ProductImages = ({ form, update }) => {
  const [url, setUrl] = useState('');
  const images = form.images || [];

  const addImage = () => {
    const trimmed = url.trim();
    if (!trimmed || images.includes(trimmed)) {
      setUrl('');
      return;
    }
    update('images', [...images, trimmed]);
    setUrl('');
  };

  const removeImage = (target) =>
    update('images', images.filter((img) => img !== target));

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Product Images
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add hosted image URLs (jpg, png, jpeg, webp). The first image is used as
        the product thumbnail.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <TextField
          label="Image URL"
          placeholder="https://…"
          variant="outlined"
          fullWidth
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addImage();
            }
          }}
        />
        <Button
          variant="outlined"
          startIcon={<AddPhotoAlternateIcon />}
          onClick={addImage}
          disabled={!url.trim()}
        >
          Add
        </Button>
      </Stack>

      {images.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No images added yet.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {images.map((img, index) => (
            <Grid item xs={6} sm={4} md={3} key={img}>
              <Card variant="outlined" sx={{ position: 'relative' }}>
                {index === 0 && (
                  <Chip
                    label="Thumbnail"
                    size="small"
                    color="primary"
                    sx={{ position: 'absolute', top: 6, left: 6 }}
                  />
                )}
                <IconButton
                  size="small"
                  aria-label="Remove image"
                  onClick={() => removeImage(img)}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'background.paper',
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
                <CardMedia
                  component="img"
                  image={img}
                  alt={`Product image ${index + 1}`}
                  sx={{ height: 140, objectFit: 'contain', bgcolor: 'background.default' }}
                />
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ProductImages;
